<template>
  <div id="n_product_detail">
    <title>{{ product.title || '产品详情' }}</title>
    <div class="pd-container">
      <!-- 加载中提示 -->
      <div v-if="loading" class="loading-tip">
        <mt-spinner type="fading-circle" color="#F6A400"></mt-spinner>
        <p>加载中...</p>
      </div>

      <!-- 主体内容，数据加载完成后显示 -->
      <div v-else class="pd-content">
        <!-- 产品展示区域（轮播所有图片） -->
        <div class="pd-section" v-if="imageList.length > 0">
          <h3 class="section-title">📸 产品展示</h3>
          <div class="pd-swipe">
            <mt-swipe :auto="4000" :show-indicators="true">
              <mt-swipe-item v-for="(img, index) in imageList" :key="index">
                <img :src="img" class="swipe-img" @click="previewImage(index)">
              </mt-swipe-item>
            </mt-swipe>
          </div>
        </div>

        <!-- 头部标题区域 -->
        <div class="pd-header">
          <h1 class="pd-title-main">{{ product.product_brand || '' }} · {{ product.product_name || '智能锁' }}</h1>
          <h2 class="pd-subtitle">{{ product.product_model || '智能锁' }}</h2>
          <div class="pd-model-tag">型号：{{ product.product_model || 'M1Max' }}</div>
        </div>

        <!-- 产品详情表格 -->
        <div class="pd-section">
          <h3 class="section-title">📋 产品详情</h3>
          <div class="info-table">
            <div class="table-row">
              <span class="row-label">产品品牌</span>
              <span class="row-value">{{ product.product_brand || '' }}</span>
            </div>
            <div class="table-row">
              <span class="row-label">产品名称</span>
              <span class="row-value">{{ product.product_name || '智能锁' }}</span>
            </div>
            <div class="table-row">
              <span class="row-label">产品特性</span>
              <span class="row-value">{{ product.product_characteristic || '智能锁' }}</span>
            </div>
          </div>
        </div>

        <!-- 功能特点区域 -->
        <div class="pd-section">
          <h3 class="section-title">✨ 功能特点</h3>
          <p class="feature-desc">{{ product.product_characteristic || '暂无详细功能描述' }}</p>
          <p class="feature-note">支持人脸、指纹、密码、NFC卡片、手机APP、应急钥匙6种便捷方式开锁，支持手机远程可视对讲。</p>
        </div>

        <!-- 视频教程区域（支持多个视频） -->
        <div class="pd-section" v-if="videoList.length > 0">
          <h3 class="section-title">🎬 安装与设置视频</h3>
          <div class="video-list">
            <div
              v-for="(video, idx) in videoList"
              :key="idx"
              class="video-item"
              @click="playVideo(video)"
            >
              <div class="video-thumb">
                <video
                  :src="video"
                  class="video-frame"
                  preload="metadata"
                  muted
                  playsinline
                  disablepictureinpicture
                  controlslist="nodownload nofullscreen noremoteplayback"
                ></video>
                <span class="play-icon">▶</span>
              </div>
              <div class="video-info">
                <p class="video-title">{{ product.product_name }} 视频{{ idx + 1 }}</p>
                <p class="video-desc">点击播放视频教程</p>
              </div>
            </div>
          </div>
        </div>

        <!-- PDF说明书下载（支持多个文件） -->
        <div class="pd-section" v-if="instructionList.length > 0">
          <h3 class="section-title">📎 说明书下载</h3>
          <div class="pdf-list">
            <div
              v-for="(file, idx) in instructionList"
              :key="idx"
              class="pdf-download"
              @click="downloadPDF(file)"
            >
              <span class="pdf-icon">📄</span>
              <span class="pdf-name">{{ product.product_name }} 说明书{{ idx + 1 }}.pdf</span>
              <span class="download-icon">⬇️</span>
            </div>
          </div>
        </div>

        <!-- 产品详情图集（纵向单列布局） -->
        <div class="pd-section" v-if="detailImageList.length > 0">
          <h3 class="section-title">📷 产品详情图集</h3>
          <div class="detail-images-list">
            <div
              v-for="(img, idx) in detailImageList"
              :key="idx"
              class="detail-img-item"
              @click="previewDetailImage(img)"
            >
              <img :src="img" class="detail-img" />
            </div>
          </div>
        </div>

        <!-- 底部品牌信息 -->
        <div class="pd-footer">
          <p class="footer-brand">{{ product.product_brand || 'VANDELO' }}</p>
          <p class="footer-model">{{ product.product_model || 'M1 Max' }}</p>
          <p class="footer-desc">{{ product.product_characteristic || '智能锁' }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Spinner, Toast, Swipe, SwipeItem } from 'mint-ui';
import 'mint-ui/lib/style.css';

export default {
  name: 'NProductDetail',
  data() {
    return {
      loading: true,
      product: {},
      imageList: [],          // 所有图片（主图+详情图）
      detailImageList: [],    // 产品详情图片
      videoList: [],          // 视频列表（支持多个）
      instructionList: []     // 说明书列表（支持多个）
    };
  },
  methods: {
    getUuidFromUrl() {
      return this.$route.query.uuid || '';
    },
    loadProductData() {
      const uuid = this.getUuidFromUrl();
      if (!uuid) {
        Toast('缺少产品标识，无法加载数据');
        this.loading = false;
        return;
      }

      this.$store.dispatch('post', {
        u: '/product_show/get',
        d: { uuid }
      }).then(res => {
        if (res.err === 0) {
          this.product = res.data || {};
          this.buildImageList();
          this.buildDetailImageList();
          this.buildVideoList();
          this.buildInstructionList();
        } else {
          Toast(res.data || '数据加载失败');
        }
        this.loading = false;
      }).catch(() => {
        Toast('网络错误，请稍后重试');
        this.loading = false;
      });
    },
    // 构建所有图片（轮播用）
    buildImageList() {
      const pics = [];
      // 主图
      if (this.product.product_pic) {
        let pic = this.product.product_pic;
        if (pic.startsWith('//')) pic = 'https:' + pic;
        pics.push(pic);
      }
      // 详情图
      if (this.product.product_detail_pic) {
        const detailPics = this.splitField(this.product.product_detail_pic);
        detailPics.forEach(p => {
          let fullPic = p.trim();
          if (fullPic.startsWith('//')) fullPic = 'https:' + fullPic;
          pics.push(fullPic);
        });
      }
      this.imageList = pics;
    },
    // 构建详情图片列表（仅详情图）
    buildDetailImageList() {
      const detailPics = [];
      if (this.product.product_detail_pic) {
        const pics = this.splitField(this.product.product_detail_pic);
        pics.forEach(p => {
          let fullPic = p.trim();
          if (fullPic.startsWith('//')) fullPic = 'https:' + fullPic;
          detailPics.push(fullPic);
        });
      }
      this.detailImageList = detailPics;
    },
    // 构建视频列表
    buildVideoList() {
      if (this.product.product_vedio) {
        this.videoList = this.splitField(this.product.product_vedio);
      } else {
        this.videoList = [];
      }
    },
    // 构建说明书列表
    buildInstructionList() {
      if (this.product.product_instructions) {
        this.instructionList = this.splitField(this.product.product_instructions);
      } else {
        this.instructionList = [];
      }
    },
    // 辅助方法：将字段解析为数组（兼容字符串逗号分隔和数组）
    splitField(value) {
      if (!value) return [];
      if (Array.isArray(value)) {
        return value.filter(item => item && item.trim() !== '');
      }
      if (typeof value === 'string') {
        return value.split(',').map(item => item.trim()).filter(item => item !== '');
      }
      return [];
    },
    // 播放视频（点击后跳转或在新窗口打开）
    playVideo(videoUrl) {
      if (videoUrl) {
        window.location.href = videoUrl;
      } else {
        Toast('视频地址无效');
      }
    },
    // 下载说明书
    downloadPDF(fileUrl) {
      if (fileUrl) {
        window.open(fileUrl, '_blank');
      } else {
        Toast('文件地址无效');
      }
    },
    previewImage(index) {
      Toast('点击图片放大（可集成图片预览组件）');
    },
    previewDetailImage(imgUrl) {
      if (imgUrl) {
        window.open(imgUrl, '_blank');
      } else {
        Toast('图片地址无效');
      }
    }
  },
  mounted() {
    this.loadProductData();
  }
};
</script>

<style scoped>
#n_product_detail {
  background-color: #f8f9fa;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  line-height: 1.5;
  color: #2c3e50;
}
.pd-container {
  max-width: 100%;
  margin: 0 auto;
}
.loading-tip {
  text-align: center;
  padding-top: 50px;
  color: #6c757d;
}
.loading-tip p {
  margin-top: 12px;
  font-size: 15px;
}
.pd-content {
  padding: 16px 14px;
}
.pd-section {
  background: white;
  border-radius: 20px;
  padding: 22px 18px;
  margin-bottom: 18px;
  box-shadow: 0 8px 20px rgba(0,0,0,0.02), 0 2px 6px rgba(0,0,0,0.05);
  transition: all 0.2s ease;
}
.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 20px 0;
  text-align: center;
  padding-bottom: 10px;
  border-bottom: 2px solid #F6A400;
  display: block;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
  padding-left: 15px;
  padding-right: 15px;
  letter-spacing: 0.5px;
}
.pd-swipe {
  height: 220px;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}
.swipe-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #f1f3f5;
}
.pd-header {
  background: linear-gradient(145deg, #1e2b3c, #15232e);
  color: white;
  padding: 28px 20px;
  border-radius: 28px;
  margin-bottom: 20px;
  text-align: center;
  box-shadow: 0 15px 25px -8px rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.1);
}
.pd-title-main {
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
  line-height: 1.3;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
}
.pd-subtitle {
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 14px 0;
  color: #F6A400;
  letter-spacing: 2px;
  text-shadow: 0 2px 8px rgba(246,164,0,0.4);
}
.pd-model-tag {
  display: inline-block;
  background: rgba(255,255,255,0.15);
  padding: 8px 20px;
  border-radius: 40px;
  font-size: 15px;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255,255,255,0.2);
  font-weight: 400;
}
.info-table {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #edf2f7;
}
.table-row {
  display: flex;
  border-bottom: 1px solid #edf2f7;
  font-size: 15px;
}
.table-row:last-child {
  border-bottom: none;
}
.row-label {
  width: 100px;
  background-color: #f8fafc;
  padding: 14px 12px;
  color: #475569;
  font-weight: 500;
  border-right: 1px solid #edf2f7;
}
.row-value {
  flex: 1;
  padding: 14px 16px;
  color: #0f172a;
  word-break: break-word;
  background: white;
  font-weight: 450;
}
.feature-desc {
  font-size: 15px;
  color: #334155;
  line-height: 1.7;
  margin: 0 0 16px 0;
  background: #f8fafc;
  padding: 16px;
  border-radius: 14px;
}
.feature-note {
  background: #fff8e7;
  padding: 14px 18px;
  border-radius: 14px;
  color: #9b6f0b;
  font-size: 14px;
  margin: 10px 0 0 0;
  border-left: 4px solid #F6A400;
  font-weight: 450;
}
.video-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.video-item {
  display: flex;
  background: #ffffff;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid #e9eef2;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.2s;
  box-shadow: 0 4px 12px rgba(0,0,0,0.03);
}
.video-item:active {
  transform: scale(0.98);
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
}
.video-thumb {
  position: relative;
  width: 130px;
  height: 90px;
  background: #1c1f26;
  border-radius: 12px 0 0 12px;
  overflow: hidden;
}
.video-frame {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #1a1e24;
  display: block;
}
.video-frame::-webkit-media-controls {
  display: none !important;
}
.video-frame::-webkit-media-controls-enclosure {
  display: none !important;
}
.play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 38px;
  height: 38px;
  background: rgba(0,0,0,0.7);
  border-radius: 50%;
  color: white;
  text-align: center;
  line-height: 40px;
  font-size: 18px;
  padding-left: 4px;
  backdrop-filter: blur(4px);
  border: 2px solid rgba(255,255,255,0.3);
  transition: background 0.2s;
}
.video-item:hover .play-icon {
  background: #F6A400;
  border-color: white;
}
.video-info {
  flex: 1;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.video-title {
  font-weight: 600;
  font-size: 16px;
  color: #0f172a;
  margin: 0 0 4px 0;
}
.video-desc {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}
.pdf-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.pdf-download {
  display: flex;
  align-items: center;
  background: #f0f7fe;
  padding: 18px 20px;
  border-radius: 18px;
  border: 1px dashed #2d7aff;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}
.pdf-download:active {
  background: #e1efff;
  border-color: #1a5bbf;
}
.pdf-icon {
  font-size: 28px;
  margin-right: 14px;
}
.pdf-name {
  flex: 1;
  font-size: 15px;
  color: #1a5bbf;
  font-weight: 500;
  word-break: break-word;
}
.download-icon {
  font-size: 22px;
  color: #1a5bbf;
}
.detail-images-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.detail-img-item {
  cursor: pointer;
  border-radius: 16px;
  overflow: hidden;
  background: #f1f3f5;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
}
.detail-img-item:active {
  transform: scale(0.98);
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}
.detail-img {
  width: 100%;
  height: auto;
  display: block;
  transition: transform 0.2s;
}
.detail-img-item:hover .detail-img {
  transform: scale(1.01);
}
.pd-footer {
  text-align: center;
  padding: 20px 0 5px;
  color: #94a3b8;
}
.footer-brand {
  font-size: 22px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
  letter-spacing: 1px;
}
.footer-model {
  font-size: 18px;
  color: #F6A400;
  margin: 0 0 4px 0;
  font-weight: 500;
}
.footer-desc {
  font-size: 13px;
  color: #94a3b8;
}
</style>

<style>
/* Mint UI 组件样式调整（非 scoped，全局生效） */
#n_product_detail .mint-swipe {
  height: 100%;
}
#n_product_detail .mint-swipe-indicator {
  background: rgba(255,255,255,0.5);
}
#n_product_detail .mint-swipe-indicator.is-active {
  background: #F6A400 !important;
}
</style>


// WEBPACK FOOTER //
// src/views/product_show.vue